// AcceptInviteForm.jsx

import React, { useState } from "react";
import { ArrowRight, Eye, EyeOff, Loader2 } from "lucide-react";
import { FormProvider } from "react-hook-form";

import RHFInput from "@/components/form/RHFInput";
import { ButtonShadcn as Button } from "@/components/ui/Button";
import { cn } from "@/lib/utils";

/**
 * AcceptInviteForm
 *
 * Reusable form component for accepting an invite
 * and setting a new password.
 *
 * Props:
 * - methods: react-hook-form methods
 * - onSubmit: form submit handler
 * - isSubmitting: loading state while submitting
 */
const AcceptInviteForm = ({ methods, onSubmit, isSubmitting }) => {
    // Toggle password visibility
    const [showPassword, setShowPassword] = useState(false);

    // Toggle confirm password visibility
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);

    return (
        <FormProvider {...methods}>
            {/* Form wrapper */}
            <form onSubmit={onSubmit} className="space-y-3 pt-1 text-sm">

                {/* Password field */}
                <div className="relative">
                    <RHFInput
                        name="password"
                        label="Password"
                        placeholder="Enter new password"
                        type={showPassword ? "text" : "password"}
                        labelClass="text-text-primary"
                        inputClass="placeholder:text-text-secondary"
                    />

                    {/* Show/Hide password button */}
                    <Button
                        type="button"
                        onClick={() => setShowPassword((prev) => !prev)}
                        className={cn(
                            "absolute right-3 top-[46px] -translate-y-1/2",
                            "inline-flex h-5 w-5 items-center justify-center",
                            "text-slate-600 hover:text-slate-900",
                            "focus:outline-none focus-visible:ring-2 focus-visible:ring-black/20 rounded-sm",
                            "bg-transparent transition-colors duration-150",
                        )}
                        aria-label={showPassword ? "Hide password" : "Show password"}
                    >
                        {showPassword ? (
                            <EyeOff size={16} />
                        ) : (
                            <Eye size={16} />
                        )}
                    </Button>
                </div>

                {/* Confirm password field */}
                <div className="relative">
                    <RHFInput
                        name="confirmPassword"
                        label="Confirm password"
                        placeholder="Confirm new password"
                        type={showConfirmPassword ? "text" : "password"}
                        labelClass="text-text-primary"
                        inputClass="placeholder:text-text-secondary"
                    />

                    {/* Show/Hide confirm password button */}
                    <span
                        onClick={() => setShowConfirmPassword((prev) => !prev)}
                        className={cn(
                            "absolute right-3 top-[46px] -translate-y-1/2",
                            "inline-flex h-5 w-5 items-center justify-center",
                            "text-slate-600 hover:text-slate-900",
                            "focus:outline-none focus-visible:ring-2 focus-visible:ring-black/20 rounded-sm",
                            "bg-transparent transition-colors duration-150",
                        )}
                        aria-label={
                            showConfirmPassword
                                ? "Hide confirm password"
                                : "Show confirm password"
                        }
                    >
                        {showConfirmPassword ? (
                            <EyeOff size={16} />
                        ) : (
                            <Eye size={16} />
                        )}
                    </span>
                </div>

                {/* Submit button */}
                <Button
                    type="submit"
                    disabled={isSubmitting}
                    className="
                        w-full rounded-lg
                        bg-gradient-to-r from-indigo-500 to-blue-600
                        hover:from-indigo-600 hover:to-blue-700
                        text-white flex items-center justify-center gap-2
                        py-2.5 text-sm transition-all duration-200
                        disabled:opacity-60 disabled:cursor-not-allowed
                    "
                >
                    {isSubmitting ? (
                        <>
                            {/* Loading spinner */}
                            <Loader2 size={16} className="animate-spin" />
                            Setting password...
                        </>
                    ) : (
                        <>
                            Accept invite
                            <ArrowRight size={16} />
                        </>
                    )}
                </Button>
            </form>
        </FormProvider>
    );
};

export default AcceptInviteForm;